import type { CartState } from "@ai-guidance/shared";
import type { NudgeTrigger } from "./text";

/**
 * Motorul de proactivitate (docs/plan-commerce-proactivity.md): decide DACĂ și
 * CÂND se arată un nudge. Nu desenează nimic și nu vorbește cu rețeaua - ia
 * semnale, aplică plafoanele, cheamă `onNudge`. Separarea nu e estetică: tot ce
 * e greu de verificat într-un browser (45 de secunde, un cooldown de 5 minute)
 * se testează aici cu ceasuri false, fără DOM.
 *
 * Principiul din doc, în ordine de precedență: un nudge ignorat nu se repetă,
 * nimic nu vorbește nechemat, iar cât timp agentul e DEJA în dialog (panou
 * deschis sau voce activă) motorul tace complet - altfel și-ar bâzâi propriul
 * client.
 */

/** Nimic în primele 20s ale sesiunii - lasă omul să respire. */
export const NUDGE_GRACE_MS = 20_000;

/** `hesitation`: cât stă pe o pagină de PRODUS fără să adauge în coș. */
export const HESITATION_DWELL_MS = 45_000;

/** `hesitation`: și fără scroll în ultimele atâtea milisecunde (citește, nu caută). */
export const HESITATION_SCROLL_QUIET_MS = 10_000;

/** `stale_cart`: coș ne-gol, atâta timp fără nicio mișcare spre coș/checkout. */
export const STALE_CART_IDLE_MS = 90_000;

/** Între două nudge-uri, oricare ar fi triggerul. */
export const NUDGE_COOLDOWN_MS = 5 * 60_000;

/** Cât stă balonul pe ecran dacă nimeni nu-l atinge. */
export const NUDGE_AUTO_HIDE_MS = 8_000;

/** Plafonul dur pe SESIUNE (tab), peste plafonul de unul pe pagină. */
export const MAX_NUDGES_PER_SESSION = 2;

/** Cât de des se reevaluează condițiile. Secunda e granularitatea utilă: pragurile sunt de zeci de secunde. */
export const NUDGE_TICK_MS = 1_000;

/**
 * Cât de des recitim coșul din magazin cât timp motorul e pornit ȘI mai poate
 * arăta ceva. Vizitatorul poate adăuga în coș din tema magazinului, nu doar
 * prin noi - fără sondajul ăsta, `stale_cart` și `exit_intent` n-ar exista
 * decât pentru coșurile pe care le-am umplut chiar noi. 30s: `stale_cart` are
 * oricum un prag de 90s, deci nu pierde nimic, iar /cart.js e same-origin și
 * ieftin.
 */
export const CART_POLL_MS = 30_000;

/** Sub atâția pixeli nu există exit-intent: pe mobil nu există mouse care să iasă prin marginea de sus. */
export const DESKTOP_MIN_WIDTH_PX = 1024;

/** Ce tip de pagină are magazinul sub picioarele vizitatorului. */
export type ShopPageKind = "product" | "collection" | "cart" | "other";

/**
 * Tipul paginii din CALE, nu din DOM: e singurul semnal disponibil înainte de
 * orice captură, e stabil între teme, și e exact cum navighează adaptorul
 * (`/products/`, `/collections/` sunt rute Shopify, nu convenții de temă).
 * Calea coșului vine din config, nu ghicită - poate fi `/en/cart` pe un magazin
 * multi-limbă, de-aia `endsWith` (aceeași regulă ca `maybeRefreshCartPage`).
 */
export function shopPageKind(path: string, cartPath: string): ShopPageKind {
  const clean = path.split(/[?#]/)[0] ?? path;
  if (clean === cartPath || clean.endsWith(cartPath)) return "cart";
  if (clean.includes("/products/")) return "product";
  if (clean.includes("/collections/")) return "collection";
  return "other";
}

/** Ce trebuie motorul să știe despre lumea din jur - injectat, ca testele să nu aibă nevoie de magazin. */
export interface ProactivityHost {
  /** Ceasul. Injectat ca să nu depindem de `Date.now` în teste cu ceasuri false. */
  now: () => number;
  /** Calea curentă (SPA-urile o schimbă fără reload - vezi `notePath`). */
  path: () => string;
  /** Calea coșului, din configul commerce. */
  cartPath: string;
  /** Coșul REAL al magazinului; `null` = necitibil acum (rețea, temă exotică). */
  readCart: () => Promise<CartState | null>;
  /** Panoul de chat e deschis? Atunci agentul e deja în dialog. */
  isPanelOpen: () => boolean;
  /** Conversație vocală activă? Idem - și acolo, v1 tace (vezi limitarea din raport). */
  isVoiceActive: () => boolean;
  /** Lățimea ferestrei, pentru exit-intent (desktop-only, decizie din doc). */
  viewportWidth: () => number;
  /** Un nudge trebuie afișat ACUM. Textul îl compune apelantul, din trigger + coș. */
  onNudge: (trigger: NudgeTrigger, cart: CartState | null) => void;
}

/**
 * Contoarele care trebuie să traverseze navigările, dar să MOARĂ cu tabul:
 * `sessionStorage`, niciodată `localStorage` sau cookies - rămânem în litera
 * lui cookies.html („zero storage persistent"). O cheie per app, un singur JSON.
 *
 * G24 (docs/legal-register.md), ÎNCHIS 9 aug 2026, decizia lui Radu - varianta
 * „memorie": cheia asta nu se scrie niciodată înainte ca vizitatorul să fi
 * interacționat EXPLICIT cu asistentul (deschiderea panoului sau prima
 * întrebare - exact `markInteractionStarted` din sdk.ts, ACELAȘI semnal care
 * decide și scrierea trail-ului de acțiuni din `actionTrail.ts` și persistarea
 * salutului în așteptare). Până la acel moment, `ProactivityEngine` ține
 * `state` DOAR în memoria instanței (vezi `interacted`/`notifyInteractionStarted`
 * mai jos) - nicio scriere aici nu are voie să se întâmple mai devreme, altfel
 * cookies §1 („nimic nu se stochează până deschideți asistentul") ar minți.
 * Prețul acceptat, descris pe larg la `notifyInteractionStarted`: pe un sit cu
 * mai multe pagini și reload-uri, plafoanele de sesiune se pierd la fiecare
 * navigare cât timp vizitatorul n-a interacționat încă - devin efectiv „per
 * pagină", nu „per sesiune". Vezi G24 pentru varianta respinsă (a patra cheie
 * în cookies §1) și de ce n-a fost aleasă (Radu: fără schimbare de documente).
 */
const STORE_PREFIX = "aig_nudge_";

interface NudgeSessionState {
  /** Câte nudge-uri s-au arătat în sesiunea asta (plafon MAX_NUDGES_PER_SESSION). */
  n: number;
  /** Momentul ultimului nudge afișat (cooldown). */
  last: number;
  /** Vizitatorul a apăsat ✕: gata pe sesiunea asta, oricare ar fi triggerul. */
  off: boolean;
  /** `exit_intent` s-a consumat (o singură dată pe sesiune, din doc). */
  exit: boolean;
}

const EMPTY_STATE: NudgeSessionState = { n: 0, last: 0, off: false, exit: false };

export function loadNudgeState(appId: string): NudgeSessionState {
  try {
    const raw = sessionStorage.getItem(STORE_PREFIX + appId);
    if (!raw) return { ...EMPTY_STATE };
    const parsed = JSON.parse(raw) as Partial<NudgeSessionState>;
    return {
      n: typeof parsed.n === "number" ? parsed.n : 0,
      last: typeof parsed.last === "number" ? parsed.last : 0,
      off: parsed.off === true,
      exit: parsed.exit === true,
    };
  } catch {
    // sessionStorage inaccesibil (cookies blocate, sandbox): motorul merge mai
    // departe fără memorie între pagini - plafonul pe PAGINĂ tot ține.
    return { ...EMPTY_STATE };
  }
}

function saveNudgeState(appId: string, state: NudgeSessionState): void {
  try {
    sessionStorage.setItem(STORE_PREFIX + appId, JSON.stringify(state));
  } catch {
    // vezi loadNudgeState
  }
}

export class ProactivityEngine {
  private state: NudgeSessionState;
  /** Momentul pornirii - baza pentru grația de 20s. */
  private readonly startedAt: number;
  private timer?: ReturnType<typeof setInterval>;
  private cart: CartState | null = null;
  private cartPolledAt = 0;
  private cartPollInFlight = false;

  /**
   * G24: până la `notifyInteractionStarted()`, `state` trăiește doar aici, în
   * memoria instanței - vezi `persist()`. Pornește mereu `false` la fiecare
   * construcție (fiecare încărcare de pagină e o instanță nouă), la fel ca
   * `interacted` din `actionTrail.installActionTrail`: chiar dacă vizitatorul
   * a interacționat deja pe o pagină anterioară din ACEEAȘI sesiune (și starea
   * veche a fost persistată atunci), pagina curentă tot așteaptă propriul
   * semnal înainte să scrie ceva - exact simetria cu actionTrail, deliberată.
   */
  private interacted = false;

  /** Ce se resetează la fiecare pagină: plafonul „max 1 nudge pe pagină" și semnalele ei. */
  private path: string;
  private pageEnteredAt: number;
  private lastScrollAt: number;
  private nudgedThisPage = false;
  private addedToCartThisPage = false;

  /**
   * Ultima mișcare a vizitatorului SPRE coș sau checkout: intrarea pe pagina
   * coșului, sau o mutație de coș făcută prin noi. `stale_cart` măsoară liniștea
   * de la ea încolo - un om care tocmai a intrat în coș nu are „coșul uitat".
   */
  private lastCartIntentAt: number;

  private readonly onScroll = (): void => {
    this.lastScrollAt = this.host.now();
  };

  /**
   * Singurul listener cu adevărat NOU din feature (doc: „Semnalele"): mouse-ul
   * iese prin marginea de SUS a viewportului - gestul de a pleca spre bara de
   * adrese sau spre alt tab. `clientY <= 0` e ce distinge ieșirea în sus de
   * simpla plimbare a mouse-ului în afara ferestrei; `relatedTarget` nul
   * înseamnă că a ieșit din document, nu că a trecut peste un iframe.
   */
  private readonly onMouseLeave = (event: MouseEvent): void => {
    if (event.clientY > 0 || event.relatedTarget) return;
    this.maybeFire("exit_intent");
  };

  constructor(
    private readonly appId: string,
    private readonly host: ProactivityHost,
  ) {
    this.state = loadNudgeState(appId);
    this.startedAt = host.now();
    this.path = host.path();
    this.pageEnteredAt = this.startedAt;
    // Nu 0: la aterizare n-a existat niciun scroll, iar un „ultimul scroll = la
    // începutul timpului" ar face condiția de liniște adevărată din prima
    // secundă. Momentul intrării e răspunsul onest la „de când e liniște".
    this.lastScrollAt = this.startedAt;
    this.lastCartIntentAt = this.startedAt;
  }

  start(): void {
    if (this.timer) return;
    // `passive`: nu atingem niciodată scroll-ul sitului clientului.
    window.addEventListener("scroll", this.onScroll, { passive: true });
    document.addEventListener("mouseleave", this.onMouseLeave);
    this.timer = setInterval(() => this.tick(), NUDGE_TICK_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    window.removeEventListener("scroll", this.onScroll);
    document.removeEventListener("mouseleave", this.onMouseLeave);
  }

  /**
   * Pagina s-a schimbat (inclusiv fără reload, pe un SPA). Resetează plafonul de
   * pagină și semnalele ei; pe pagina coșului marchează intenția, deci
   * `stale_cart` repornește numărătoarea de acolo.
   */
  notePath(path: string): void {
    if (path === this.path) return;
    this.path = path;
    this.pageEnteredAt = this.host.now();
    this.lastScrollAt = this.pageEnteredAt;
    this.nudgedThisPage = false;
    this.addedToCartThisPage = false;
    if (shopPageKind(path, this.host.cartPath) === "cart") this.lastCartIntentAt = this.pageEnteredAt;
  }

  /**
   * Vizitatorul a pus ceva în coș de pe pagina asta. Două efecte, ambele din
   * doc: `hesitation` nu mai are ce descrie (nu mai ezită), iar `stale_cart`
   * repornește - coșul tocmai a fost atins, nu uitat.
   */
  noteCartActivity(): void {
    this.addedToCartThisPage = true;
    this.lastCartIntentAt = this.host.now();
    // Starea proaspătă se va citi la următorul sondaj; până atunci nu inventăm
    // un coș - un text cu cifre greșite e mai rău decât un nudge întârziat.
    this.cartPolledAt = 0;
  }

  /**
   * ✕ pe balon: gata cu nudge-urile pe sesiunea asta (doc). Dacă vizitatorul nu
   * a interacționat încă deloc cu asistentul (n-a deschis panoul, n-a întrebat
   * nimic - un ✕ pe un nudge NU e o interacțiune cu asistentul, e un refuz al
   * lui), efectul rămâne doar în memorie: ține pe pagina curentă (G24), dar nu
   * traversează un reload. Acceptat deliberat, vezi `notifyInteractionStarted`.
   */
  dismiss(): void {
    this.state = { ...this.state, off: true };
    this.persist();
  }

  /**
   * Semnalul de interacțiune explicită - sdk.ts îl trimite din
   * `markInteractionStarted()`, ACELAȘI punct unic care persistă salutul în
   * așteptare și scrie trail-ul de acțiuni din memorie (actionTrail.flush()).
   * Nu un semnal nou inventat pentru proactivitate: e pragul deja folosit de
   * restul SDK-ului pentru „de-acum ePrivacy art. 5(3) scutește de consimțământ,
   * fiindcă stocarea a devenit strict necesară unui serviciu cerut explicit de
   * vizitator" (vezi chatSession.ts). Migrarea scrie ACUM tot ce s-a acumulat
   * în `state` până în acest moment - un nudge afișat/respins înainte de
   * interacțiune nu se pierde, doar se scrie cu întârziere.
   *
   * Idempotentă: a doua chemare (ex. o a doua întrebare) nu mai scrie nimic în
   * plus aici - `maybeFire`/`dismiss` de acum înainte scriu direct prin
   * `persist()`.
   */
  notifyInteractionStarted(): void {
    if (this.interacted) return;
    this.interacted = true;
    saveNudgeState(this.appId, this.state);
  }

  /**
   * Singurul loc care scrie efectiv `state` în sessionStorage - poarta G24.
   * Înainte de interacțiune, actualizează doar `this.state` (apelantul a
   * făcut deja asta) și nu atinge storage-ul: plafoanele de sesiune rămân
   * corecte în MEMORIE pentru restul paginii curente (de-aia `nudgedThisPage`
   * și `canNudge` tot funcționează), dar nu traversează un reload - exact
   * degradarea descrisă la `STORE_PREFIX`.
   */
  private persist(): void {
    if (!this.interacted) return;
    saveNudgeState(this.appId, this.state);
  }

  /** Ultima stare de coș citită - folosită de apelant ca să compună textul. */
  lastCart(): CartState | null {
    return this.cart;
  }

  /**
   * Poarta comună a tuturor triggerelor. Deliberat una singură: fiecare regulă
   * de bun-simț din doc („max 2 pe sesiune", „cooldown", „nu peste un dialog în
   * curs") trebuie să fie imposibil de uitat la adăugarea unui trigger nou.
   */
  private canNudge(now: number): boolean {
    if (this.state.off) return false;
    if (this.state.n >= MAX_NUDGES_PER_SESSION) return false;
    if (this.nudgedThisPage) return false;
    if (now - this.startedAt < NUDGE_GRACE_MS) return false;
    if (this.state.last > 0 && now - this.state.last < NUDGE_COOLDOWN_MS) return false;
    // Agentul e deja în dialog: și-ar întrerupe propria conversație.
    if (this.host.isPanelOpen() || this.host.isVoiceActive()) return false;
    return true;
  }

  private maybeFire(trigger: NudgeTrigger): void {
    const now = this.host.now();
    if (!this.canNudge(now)) return;
    if (trigger === "exit_intent") {
      if (this.state.exit) return;
      if (this.host.viewportWidth() < DESKTOP_MIN_WIDTH_PX) return;
      // Fără coș, „înainte să pleci" n-are despre ce vorbi.
      if (!this.cart || this.cart.itemCount === 0) return;
      this.state = { ...this.state, exit: true };
    }
    this.nudgedThisPage = true;
    this.state = { ...this.state, n: this.state.n + 1, last: now };
    this.persist();
    this.host.onNudge(trigger, this.cart);
  }

  /** Un ciclu de evaluare. Public pentru teste; în producție îl cheamă `setInterval`. */
  tick(): void {
    const now = this.host.now();
    // Navigarea SPA nu anunță pe nimeni: o observăm aici, nu prin încă un patch
    // pe `history` (sdk.ts și actionTrail.ts au deja câte unul).
    this.notePath(this.host.path());
    if (!this.canNudge(now)) return;
    this.pollCart(now);

    const kind = shopPageKind(this.path, this.host.cartPath);
    const cartCount = this.cart?.itemCount ?? 0;

    // `hesitation` - citește un produs, nu-l pune în coș, și nici nu mai derulează.
    if (
      kind === "product" &&
      !this.addedToCartThisPage &&
      now - this.pageEnteredAt >= HESITATION_DWELL_MS &&
      now - this.lastScrollAt >= HESITATION_SCROLL_QUIET_MS
    ) {
      this.maybeFire("hesitation");
      return;
    }

    // `stale_cart` - coș ne-gol, uitat. Nu pe pagina coșului: acolo omul e chiar
    // la coș, iar „ai produse în coș" ar fi o observație despre ecranul lui.
    if (kind !== "cart" && cartCount > 0 && now - this.lastCartIntentAt >= STALE_CART_IDLE_MS) {
      this.maybeFire("stale_cart");
    }
  }

  /**
   * Sondajul de coș, cu propria lui frecvență (vezi CART_POLL_MS) și fără
   * suprapunere: o citire lentă nu are voie să pornească alta la fiecare
   * secundă. `void`, nu `await`: tick-ul nu așteaptă rețeaua - starea sosită
   * târziu e folosită la tick-ul următor.
   */
  private pollCart(now: number): void {
    if (this.cartPollInFlight) return;
    if (this.cartPolledAt !== 0 && now - this.cartPolledAt < CART_POLL_MS) return;
    this.cartPollInFlight = true;
    const before = this.cart?.itemCount ?? null;
    void this.host
      .readCart()
      .then((cart) => {
        // Coșul a crescut între două sondaje: vizitatorul a adăugat ceva din
        // tema magazinului, fără să treacă prin noi. E aceeași informație ca
        // `noteCartActivity`, doar pe cealaltă cale - fără ea, un om care
        // tocmai a apăsat „Adaugă în coș" pe pagina produsului ar primi, 45 de
        // secunde mai târziu, un nudge care îl întreabă dacă are nelămuriri
        // despre exact produsul pe care l-a cumpărat.
        if (before !== null && (cart?.itemCount ?? 0) > before) {
          this.addedToCartThisPage = true;
          this.lastCartIntentAt = this.host.now();
        }
        this.cart = cart;
      })
      .catch(() => {
        // O citire eșuată nu are voie să golească un coș cunoscut: mai bine o
        // stare puțin veche decât un nudge care afirmă „coș gol".
      })
      .finally(() => {
        this.cartPollInFlight = false;
        this.cartPolledAt = this.host.now();
      });
  }
}
